import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import clsx from 'clsx';
import { type FC, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

export interface ShoppingCartSupplierCardProps {
  id: string;
  name: string;
  totalFormatted: string;
  itemCount: number;
  selected?: boolean;
  onSelectionChange?: (id: string, selected: boolean) => void;
  onSupplierInfoClick?: (id: string) => void;
  showSupplierInfo?: boolean;
  className?: string;
}

const ShoppingCartSupplierCard: FC<ShoppingCartSupplierCardProps> = ({
  id,
  name,
  totalFormatted,
  itemCount,
  selected = false,
  onSelectionChange,
  onSupplierInfoClick,
  showSupplierInfo = true,
  className,
}): ReactElement => {
  const { t } = useTranslation();

  const handleCheckboxChange = (event: CheckboxChangeEvent) => {
    onSelectionChange?.(id, Boolean(event.value));
  };

  return (
    <article
      className={clsx(
        'flex h-[85px] w-[230px] min-w-[230px] shrink-0 items-start gap-2 rounded-lg border bg-white p-3',
        className
      )}
      style={{ borderColor: selected ? '#3F6578' : '#BABABA' }}
      data-testid={`shopping-cart-supplier-card-${id}`}
    >
      <Checkbox
        aria-label={t('Select Supplier')}
        checked={selected}
        onChange={handleCheckboxChange}
        className={clsx(
          'shrink-0 self-start',
          '[&_.k-checkbox:checked]:!border-[rgba(63,101,120,1)] [&_.k-checkbox:checked]:!bg-[rgba(63,101,120,1)]'
        )}
      />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-0.5 self-stretch">
        <h3
          className="truncate text-sm font-semibold leading-tight text-[#111111]"
          data-tooltip-id="global-tooltip"
          data-tooltip-content={name}
        >
          {name}
        </h3>

        <div className="flex min-w-0 items-baseline justify-between gap-2">
          <p className="truncate text-sm font-normal leading-tight text-[#111111]">
            {totalFormatted}
          </p>
          <span className="shrink-0 whitespace-nowrap text-xs font-normal leading-tight text-[#6B7280]">
            {t('In Cart')} : {itemCount}
          </span>
        </div>

        <div className="h-5">
          {showSupplierInfo ? (
            <button
              type="button"
              className="w-fit p-0 text-left text-sm font-medium leading-tight text-info hover:underline focus:outline-2 focus:outline-black"
              onClick={() => onSupplierInfoClick?.(id)}
              aria-label={`${t('Supplier Info')} - ${name}`}
            >
              {t('Supplier Info')}
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
};

export default ShoppingCartSupplierCard;
